/**
 * User-portal layout — chrome shared by every authenticated non-admin route.
 *
 *   ┌──────────────────────────────────────────────────┐
 *   │ TopBar                                           │  48px
 *   ├─────────┬────────────────────────────────────────┤
 *   │         │  OpenAINotConfiguredBanner (if shown)  │
 *   │ UserSidebar (Chats / Sheets)                     │
 *   │ 280px   │  Page content (route Outlet)           │
 *   └─────────┴────────────────────────────────────────┘
 *
 * Mirrors AdminLayout's structure for visual consistency.
 */
import { Suspense, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { RouteFallback } from '../../components/RouteFallback';
import { CommandPalette } from '../../components/shell/CommandPalette';
import { OpenAINotConfiguredBanner } from '../../components/shell/OpenAINotConfiguredBanner';
import { TopBar } from '../../components/shell/TopBar';
import { UserSidebar } from '../../components/shell/UserSidebar';

export function UserLayout(): JSX.Element {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const { pathname } = useLocation();
  // Surfaces that own the full width, with no feature-menu rail beside them:
  //   /odoo — a self-contained chat workspace with its own session rail, so the
  //           shared menu would be a redundant second rail (same reasoning as
  //           /chat swapping the menu for its own Chats rail).
  //   /mail — a top-bar workspace rather than a left-rail module, so the mailbox
  //           gets the whole screen.
  //   /automation — a self-contained chat workspace with its own session rail,
  //           same reasoning as /odoo.
  const hideSidebar =
    pathname.startsWith('/odoo') ||
    pathname.startsWith('/mail') ||
    pathname.startsWith('/automation');

  return (
    <div className="fixed inset-0 flex flex-col overflow-hidden bg-paper text-ink">
      <TopBar onOpenCommandPalette={() => setPaletteOpen(true)} />
      <div className="flex min-h-0 flex-1">
        {!hideSidebar && <UserSidebar />}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <OpenAINotConfiguredBanner />
          <main className="min-h-0 flex-1 overflow-auto">
            <Suspense fallback={<RouteFallback />}>
              <Outlet />
            </Suspense>
          </main>
        </div>
      </div>
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  );
}
